import {BehavioralPopup} from '@/components/behavioralPopup/behavioralPopup';
import {ExitIntentPopup} from '@/components/exitIntentPopup/exitIntentPopup';
import {useEventViewTrigger} from '@/hooks/useEventViewTrigger';
import {usePopupManager} from '@/hooks/usePopupManager';
import type {PopupType} from '@/hooks/usePopupManager';
import {popupState} from '@/lib/popupState';
import {createContext, useCallback, useContext} from 'react';
import type {ReactNode} from 'react';

interface PopupContextValue {
  activePopup: PopupType | null;
  requestPopup: (type: PopupType) => boolean;
  dismissPopup: () => void;
  convertPopup: () => void;
  isBlocked: boolean;
  /** Record a user viewing an event listing (triggers behavioral popup after 3 views) */
  recordEventView: () => void;
  /**
   * Explicitly request the behavioral popup to show now, if the event-view
   * threshold has been reached. Safe to call multiple times — the popup
   * manager ensures it only shows once per session.
   */
  fireBehavioralPopup: () => void;
  /** Whether this is the visitor's first ever session */
  isFirstSession: boolean;
  /** Number of event listing views recorded this session */
  eventsViewed: number;
}

const PopupContext = createContext<PopupContextValue | null>(null);

/**
 * Provides popup conflict management to the component tree.
 * Wrap this around the app so any popup component can request
 * display permission and record dismissals/conversions.
 *
 * Exposes `recordEventView` so the search page can track event interactions
 * and `fireBehavioralPopup` so callers can choose the right moment to show it.
 */
function PopupProvider({children}: {children: ReactNode}) {
  const manager = usePopupManager();
  const {requestPopup} = manager;
  const {triggered: behavioralTriggered, recordView, viewCount} = useEventViewTrigger({
    threshold: 3,
    enabled: !manager.isBlocked,
  });

  const fireBehavioralPopup = useCallback(() => {
    if (!behavioralTriggered) return;
    const granted = requestPopup('behavioral');
    if (granted) {
      popupState.recordShown('behavioral');
    }
  }, [behavioralTriggered, requestPopup]);

  const contextValue: PopupContextValue = {
    ...manager,
    recordEventView: recordView,
    fireBehavioralPopup,
    eventsViewed: viewCount,
  };

  return (
    <PopupContext.Provider value={contextValue}>
      {children}
      <BehavioralPopup triggered={behavioralTriggered} />
      <ExitIntentPopup />
    </PopupContext.Provider>
  );
}

/**
 * Access the popup manager from any component.
 * Must be used within a PopupProvider.
 */
function usePopupContext(): PopupContextValue {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopupContext must be used within a PopupProvider');
  }
  return context;
}

export {PopupProvider, usePopupContext};
